import { useQuery } from '@tanstack/react-query';
import { apiFetch, useError } from './api';

export const PARENT_DETAIL_QUERY_KEY = ['parentDetail'];

const toComparableId = (value) =>
  value === null || value === undefined ? null : String(value);

const normalizeGroup = (group) => {
  if (!group) {
    return null;
  }

  const groupId = toComparableId(group.groupId ?? group.id);
  const groupName = group.groupName ?? group.title ?? null;

  if (groupId === null && !groupName) {
    return null;
  }

  return {
    ...group,
    groupId,
    groupName,
  };
};

const normalizeModuleItems = (items = []) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          if (!item) {
            return null;
          }

          const itemId = item.itemId ?? item.id ?? null;

          if (itemId === null || itemId === undefined) {
            return null;
          }

          return {
            ...item,
            itemId: String(itemId),
            itemType: item.itemType ?? item.type ?? null,
            dateCompleted: item.dateCompleted ?? null,
            rating: item.rating ?? null,
          };
        })
        .filter(Boolean)
    : [];

const normalizeModules = (modules = []) =>
  Array.isArray(modules)
    ? modules
        .map((module, index) => {
          if (!module) {
            return null;
          }

          const moduleNumber = Number(
            module.moduleNumber ?? module.module ?? index + 1,
          );

          if (!Number.isInteger(moduleNumber) || moduleNumber < 1) {
            return null;
          }

          return {
            ...module,
            moduleNumber,
            engagementScore: module.engagementScore ?? null,
            dateCompleted: module.dateCompleted ?? null,
            items: normalizeModuleItems(module.items),
            practiceReview: module.practiceReview
              ? {
                  ...module.practiceReview,
                  date: module.practiceReview.date ?? null,
                  name: module.practiceReview.name ?? null,
                  value: module.practiceReview.value ?? null,
                }
              : null,
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.moduleNumber - right.moduleNumber)
    : [];

export const normalizeParentDetailData = (parentDetail = {}) => {
  const rawParentInfo = parentDetail.parentInfo ?? parentDetail;
  const group = normalizeGroup(parentDetail.group);
  const modules = normalizeModules(parentDetail.modules ?? parentDetail.module);

  return {
    ...parentDetail,
    parentInfo: {
      ...rawParentInfo,
      userId: toComparableId(rawParentInfo.userId ?? rawParentInfo.id),
      username: rawParentInfo.username ?? rawParentInfo.userName ?? '',
      firstName: rawParentInfo.firstName ?? null,
      lastName: rawParentInfo.lastName ?? null,
      email: rawParentInfo.email ?? '',
      phone: rawParentInfo.phone ?? '',
      isArchived: rawParentInfo.isArchived ?? false,
    },
    group,
    groups: group ? [group] : [],
    modules,
    practices: Array.isArray(parentDetail.practices) ? parentDetail.practices : [],
    progress: parentDetail.progress ?? null,
  };
};

export const getParentDetail = async (parentId) => {
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/admin/parent/${parentId}`,
    {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return response.json();
};

export const useParentDetailQuery = (parentId, options = {}) => {
  const { checkError } = useError();
  const { onError, ...queryOptions } = options;

  return useQuery({
    queryKey: [...PARENT_DETAIL_QUERY_KEY, parentId],
    queryFn: () => getParentDetail(parentId),
    select: normalizeParentDetailData,
    enabled: Boolean(parentId),
    retry: false,
    ...queryOptions,
    onError: (error) => {
      checkError(error);
      onError?.(error);
    },
  });
};
